import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@/registry/reapit/lib/slot'

import { cn } from '@/registry/reapit/lib/utils'
import { Separator } from '@/registry/reapit/ui/separator'

/*
 * ButtonGroup — DS-aligned implementation (Phase 3A)
 *
 * DS source: https://design.reapit.com.au — Button group (pageId 8403093)
 *
 * DS defines button group as a container that visually connects sibling buttons.
 * Supported layouts: standard (natural widths), equal-width, icon-only, mixed.
 * Supported orientations: horizontal (default), vertical.
 *
 * Implementation notes:
 * - Individual buttons inside the group retain their own variant/size.
 * - Border-joining (remove shared radius + border) is achieved via :not(:first-child) /
 *   :not(:last-child) selectors on the group container.
 * - Icon default size: size-5 (20px = DS icon-md) per Principal decision (plan/04).
 * - ButtonGroupText is a Reapit extension (DS does not define a text-only slot in a group).
 */

const buttonGroupVariants = cva(
  "flex w-fit items-stretch has-[>[data-slot=button-group]]:gap-2 [&>*]:focus-visible:relative [&>*]:focus-visible:z-10 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  {
    variants: {
      orientation: {
        horizontal:
          '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none',
        vertical:
          'flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none',
      },
    },
    defaultVariants: {
      orientation: 'horizontal',
    },
  },
)

function ButtonGroup({
  className,
  orientation,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof buttonGroupVariants>) {
  return (
    <div
      role="group"
      data-slot="button-group"
      data-orientation={orientation}
      className={cn(buttonGroupVariants({ orientation }), className)}
      {...props}
    />
  )
}

/*
 * ButtonGroupText — a static text/label slot within a button group.
 * Reapit extension — not in DS spec. Uses DS icon-md (size-5 = 20px) for any icons.
 */
function ButtonGroupText({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & {
  asChild?: boolean
}) {
  const Comp = asChild ? Slot.Root : 'div'

  return (
    <Comp
      className={cn(
        "flex items-center gap-2 rounded-md border bg-muted px-4 text-sm font-medium shadow-xs [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5",
        className,
      )}
      {...props}
    />
  )
}

/*
 * ButtonGroupSeparator — a visual divider between button group items.
 * Uses the input border colour (neutral-150) so it blends with joined button borders.
 */
function ButtonGroupSeparator({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="button-group-separator"
      orientation={orientation}
      className={cn('relative m-0! self-stretch bg-input data-[orientation=vertical]:h-auto', className)}
      {...props}
    />
  )
}

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText, buttonGroupVariants }
